import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Table, 
  Input, 
  Button, 
  Typography, 
  Space, 
  Progress, 
  message, 
  Divider,
  Card,
  Spin
} from 'antd';
import { SaveOutlined, CloseOutlined, RobotOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { translationsService } from '../../api/translations';
import { translationProjectsService } from '../../api/translationProjects';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface TranslationResult {
  key: string;
  languageId: string;
  languageName: string;
  languageCode: string;
  originalValue?: string;
  translatedValue: string;
  success: boolean;
  error?: string;
}

interface BulkTranslateModalProps {
  visible: boolean;
  onCancel: () => void;
  results: TranslationResult[];
  onSaveTranslations?: (translations: TranslationResult[]) => void;
  onStartTranslation?: () => void;
  isLoading?: boolean;
  totalKeys?: number;
  currentTranslatingText?: string;
  processedCount?: number;
  selectedProjectId?: string | null;
}

const BulkTranslateModal: React.FC<BulkTranslateModalProps> = ({ 
  visible, 
  onCancel, 
  results, 
  onSaveTranslations,
  onStartTranslation,
  isLoading = false,
  totalKeys = 0,
  currentTranslatingText = '',
  processedCount = 0,
  selectedProjectId
}) => {
  const [editableResults, setEditableResults] = useState<TranslationResult[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (results) {
      setEditableResults([...results]);
    }
  }, [results]);

  // Debug log for props and force re-render
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('BulkTranslateModal props changed:', { 
        totalKeys, 
        processedCount, 
        isLoading,
        percent: totalKeys > 0 ? Math.round((processedCount / totalKeys) * 100) : 0,
        timestamp: Date.now()
      });
    }
  }, [totalKeys, processedCount, isLoading]);

  const updateTranslationMutation = useMutation({
    mutationFn: async (translation: { languageId: string; key: string; value: string }) => {
      return translationsService.updateTranslation(translation.languageId, translation.key, { 
        value: translation.value 
      });
    },
    onSuccess: () => {
      // Don't automatically invalidate - let parent handle it
      // queryClient.invalidateQueries({ queryKey: ['all-translations'] });
    },
  });

  const handleEdit = (index: number, value: string) => {
    setEditableResults(prev => prev.map((item, i) => 
      i === index 
        ? { ...item, translatedValue: value }
        : item
    ));
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);

      const translationsToSave = editableResults.filter(item => item.success && item.translatedValue);

      if (translationsToSave.length === 0) {
        message.warning('Нет переводов для сохранения');
        return;
      }

      // Use batch API if we have a selected project ID
      if (selectedProjectId) {
        console.log('🚀 Using batch save for project:', selectedProjectId);
        
        const batchTranslations = translationsToSave.map(translation => ({
          languageId: translation.languageId,
          key: translation.key,
          value: translation.translatedValue
        }));
        
        const result = await translationProjectsService.batchSaveProjectTranslations(
          selectedProjectId,
          batchTranslations
        );
        
        console.log('🎉 Batch save result:', result);
        
        if (result.errors && result.errors.length > 0) {
          console.warn('⚠️ Batch save errors:', result.errors);
          message.warning(`Сохранено ${result.saved} переводов, ${result.errors.length} ошибок`);
        } else {
          message.success(`Батч сохранение: ${result.saved} переводов`);
        }
      } else {
        console.log('🐌 Fallback to individual saves (no project selected)');
        
        // Fallback to individual saves
        const savePromises = translationsToSave.map(translation => 
          updateTranslationMutation.mutateAsync({
            languageId: translation.languageId,
            key: translation.key,
            value: translation.translatedValue
          })
        );

        await Promise.all(savePromises);
        message.success(`Сохранено ${translationsToSave.length} переводов`);
      }

      if (onSaveTranslations) {
        onSaveTranslations(translationsToSave);
      }

      onCancel();
    } catch (error) {
      console.error('Save translations error:', error);
      message.error('Ошибка при сохранении переводов');
    } finally {
      setSaving(false);
    }
  };

  const successCount = editableResults.filter(item => item.success).length;
  const totalCount = editableResults.length;
  const successRate = totalCount > 0 ? (successCount / totalCount) * 100 : 0;

  const columns = [
    {
      title: 'Ключ',
      dataIndex: 'key',
      key: 'key',
      width: 200,
      fixed: 'left' as const,
      render: (text: string) => (
        <Text code style={{ fontSize: '11px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Язык',
      dataIndex: 'languageName',
      key: 'language', 
      width: 120,
      render: (text: string, record: TranslationResult) => (
        <Space direction="vertical" size="small">
          <Text strong style={{ fontSize: '12px' }}>{text}</Text>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            ({record.languageCode})
          </Text>
        </Space>
      )
    },
    {
      title: 'Оригинал',
      dataIndex: 'originalValue',
      key: 'original',
      width: 200,
      render: (text: string) => (
        <Text style={{ fontSize: '11px' }} type="secondary">
          {text || 'Нет значения'}
        </Text>
      )
    },
    {
      title: 'AI Перевод',
      dataIndex: 'translatedValue',
      key: 'translated',
      width: 250,
      render: (text: string, record: TranslationResult, index: number) => {
        if (!record.success) {
          return (
            <div>
              <Text type="danger" style={{ fontSize: '11px' }}>
                Ошибка перевода
              </Text>
              {record.error && (
                <div style={{ color: '#ff4d4f', fontSize: '10px', marginTop: 2 }}>
                  {record.error}
                </div>
              )}
            </div>
          );
        }

        return (
          <TextArea
            value={text}
            onChange={(e) => handleEdit(index, e.target.value)}
            autoSize={{ minRows: 1, maxRows: 3 }}
            style={{ 
              fontSize: '11px',
              backgroundColor: '#f6ffed' // Green background for AI translations
            }}
          />
        );
      }
    }
  ];

  return (
    <Modal
      title={
        <Space>
          {isLoading ? <Spin size="small" /> : <RobotOutlined />}
          <Text strong>Результаты AI перевода</Text>
        </Space>
      }
      open={visible}
      onCancel={() => {
        if (isLoading) {
          message.warning('Перевод в процессе, пожалуйста подождите...');
          return;
        }
        onCancel();
      }}
      width={1200}
      style={{ top: 20 }}
      closable={!isLoading}
      maskClosable={!isLoading}
      footer={
        editableResults.length === 0 && !isLoading ? [
          // Before translation starts - show start button
          <Button 
            key="cancel" 
            onClick={() => {
              if (isLoading) {
                message.warning('Перевод в процессе, пожалуйста подождите...');
                return;
              }
              onCancel();
            }}
            icon={<CloseOutlined />}
          >
            Отмена
          </Button>,
          <Button
            key="start"
            type="primary"
            icon={<RobotOutlined />}
            onClick={onStartTranslation}
            style={{
              background: 'linear-gradient(135deg, #8B5CF6, #EF4444)',
              borderColor: 'transparent',
            }}
          >
            Перевести пустые с помощью AI ({totalKeys} ключей)
          </Button>
        ] : [
          // After translation or during loading - show save button
          <Button 
            key="cancel" 
            onClick={onCancel} 
            disabled={isLoading}
            icon={<CloseOutlined />}
          >
            Отмена
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveAll}
            loading={saving}
            disabled={editableResults.length === 0 || isLoading}
          >
            Сохранить все переводы
          </Button>
        ]
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Progress indicator */}
        {isLoading && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Обработка переводов...</Text>
                <Text type="secondary">{processedCount} из {totalKeys}</Text>
              </div>
              
              <Progress 
                percent={totalKeys > 0 ? Math.round((processedCount / totalKeys) * 100) : 0} 
                status={totalKeys > 0 && processedCount === totalKeys ? 'success' : 'active'}
                strokeColor={{
                  '0%': '#8B5CF6',
                  '100%': '#EF4444',
                }}
              />
              
              {/* Debug info */}
              {process.env.NODE_ENV === 'development' && (
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  Debug: {processedCount}/{totalKeys} = {totalKeys > 0 ? Math.round((processedCount / totalKeys) * 100) : 0}%
                </Text>
              )}
              
              {currentTranslatingText && (
                <Card 
                  size="small" 
                  style={{ 
                    backgroundColor: '#f6ffed', 
                    border: '1px solid #b7eb8f',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Space align="center">
                    <div style={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#52c41a',
                      animation: 'pulse 1.5s infinite'
                    }} />
                    <Text style={{ fontSize: '12px' }}>
                      <Text strong>Переводится:</Text> {currentTranslatingText}
                    </Text>
                  </Space>
                  
                  <style>{`
                    @keyframes pulse {
                      0% { opacity: 1; }
                      50% { opacity: 0.5; }
                      100% { opacity: 1; }
                    }
                  `}</style>
                </Card>
              )}
            </Space>
          </Card>
        )}

        {/* Statistics */}
        {!isLoading && (
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Статистика переводов</Text>
                <Text type="success">
                  {successCount} из {totalCount} успешно
                </Text>
              </div>
              <Progress 
                percent={successRate} 
                status={successRate === 100 ? 'success' : 'active'}
                showInfo={false}
                strokeColor="#52c41a"
              />
            </Space>
          </Card>
        )}

        <Divider style={{ margin: '12px 0' }} />

        {/* Content based on state */}
        {editableResults.length === 0 && !isLoading ? (
          // Before translation starts - show information
          <Card style={{ textAlign: 'center', padding: 32 }}>
            <Space direction="vertical" size="large">
              <RobotOutlined style={{ fontSize: 48, color: '#8B5CF6' }} />
              <div>
                <Title level={4}>AI Перевод готов к запуску</Title>
                <Text type="secondary">
                  Найдено {totalKeys} пустых переводов для обработки.
                  <br />
                  Нажмите кнопку &ldquo;Перевести&rdquo; чтобы начать автоматический перевод.
                </Text>
              </div>
            </Space>
          </Card>
        ) : (
          // Results Table
          <Table
            columns={columns}
            dataSource={editableResults}
            scroll={{ x: 800, y: 400 }}
            pagination={{
              pageSize: 10,
              size: 'small',
              showSizeChanger: false
            }}
            rowKey={(record, index) => `${record.key}-${record.languageId}-${index}`}
            size="small"
            loading={isLoading}
          />
        )}
      </Space>
    </Modal>
  );
};

export default BulkTranslateModal;
